/**
 * The Game tab's skip decisions, pure (ISSUE-1044, SUR-R87). Whether the fan
 * may skip or undo and how many skips are left come from the server through
 * /round/get-board; this model only picks which control to show and maps a
 * refusal code to its note.
 */
import { extractErrorCode } from "../../errors/apiErrorBus";
import type { typeBoardData } from "../pick/pickBoardModel";
import {
  SURVIVOR_SKIP_HAS_PICKS_NOTE,
  SURVIVOR_SKIP_NONE_LEFT_NOTE,
  SURVIVOR_SKIP_ROUND_LOCKED_NOTE,
  SURVIVOR_UNDO_NOT_FOUND_NOTE,
  SURVIVOR_UNDO_ROUND_LOCKED_NOTE,
  survivorSkipsLeftLabel,
} from "./survivorGameCopy";
import type { typeSurvivorGameView } from "./useSurvivorGameData";

export type typeSkipRowView =
  | { kind: "declare"; skipsLeftLabel: string }
  | { kind: "undo"; skipsLeftLabel: string };

export type typeSkipOutcome =
  | { kind: "ok" }
  | { kind: "inline"; code: string; note: string }
  | { kind: "modal"; code: string };

export function resolveSkipRowView(input: {
  view: typeSurvivorGameView;
  boardData: typeBoardData | null;
  stagedPickCount: number;
}): typeSkipRowView | null {
  const { view, boardData, stagedPickCount } = input;
  if (boardData === null) return null;
  const skipsLeftLabel = survivorSkipsLeftLabel(
    boardData.skipsRemaining ?? null,
  );
  if (
    view.kind === "open" &&
    boardData.canDeclareSkip === true &&
    stagedPickCount === 0
  ) {
    return { kind: "declare", skipsLeftLabel };
  }
  if (view.kind === "skipped" && boardData.canUndoSkip === true) {
    return { kind: "undo", skipsLeftLabel };
  }
  return null;
}

export function classifySkipRoundResult(result: unknown): typeSkipOutcome {
  const code = extractErrorCode(result);
  if (code === null) return { kind: "ok" };
  if (code === "ROUND_LOCKED") {
    return { kind: "inline", code, note: SURVIVOR_SKIP_ROUND_LOCKED_NOTE };
  }
  if (code === "PICKS_ALREADY_SUBMITTED") {
    return { kind: "inline", code, note: SURVIVOR_SKIP_HAS_PICKS_NOTE };
  }
  if (code === "NO_SKIPS_LEFT") {
    return { kind: "inline", code, note: SURVIVOR_SKIP_NONE_LEFT_NOTE };
  }
  return { kind: "modal", code };
}

export function classifyUndoSkipResult(result: unknown): typeSkipOutcome {
  const code = extractErrorCode(result);
  if (code === null) return { kind: "ok" };
  if (code === "ROUND_LOCKED") {
    return { kind: "inline", code, note: SURVIVOR_UNDO_ROUND_LOCKED_NOTE };
  }
  if (code === "NOT_FOUND") {
    return { kind: "inline", code, note: SURVIVOR_UNDO_NOT_FOUND_NOTE };
  }
  return { kind: "modal", code };
}
